import React from 'react';
import { formatDateOnly, useFormatUSD } from '../../../utils/formatting';
import ProductImage from './ProductImage';
// import { PdfIcon, CheckCircleIcon } from './Icons';
import { SHOP_URL } from '../../../utils/constants';
import { canClickImage, RESTRICTED_VENDORS } from '../../../utils/constants';
import { openPortalPdf } from '../../../utils/utils';
import { PdfIcon, CheckCircleIcon } from '../../../utils/icons';
import { HiOutlineTrash } from 'react-icons/hi2';

function openPdfLink(event, url, title) {
  event.preventDefault();
  openPortalPdf(url, title);
}

const normalizeAttrKey = (key) => key.toLowerCase().replace(/_/g, ' ').trim();

const ATTRIBUTE_KEY_FILTER = (key) => {
  const k = normalizeAttrKey(key);
  return k !== 'installation manual' && k !== 'packing list';
};
const formatAttrKey = (key) => key.replace('_LeadTime', 'Lead Time').replace(/_/g, ' ').trim();
const formatAttrValue = (value) => value.replace(/_/g, ' ').trim();

function formatFulfillmentDisplayStatus(status) {
  if (!status) return '';
  return status.replace(/_/g, ' ').toLowerCase();
}

function openTrackingPopup(url) {
  const width = Math.min(1100, window.screen.availWidth - 40);
  const height = Math.min(800, window.screen.availHeight - 80);
  const left = Math.max(0, Math.round((window.screen.availWidth - width) / 2));
  const top = Math.max(0, Math.round((window.screen.availHeight - height) / 2));
  const popup = window.open(
    url,
    'willygoat-tracking',
    `width=${width},height=${height},left=${left},top=${top},scrollbars=yes,resizable=yes`,
  );
  if (popup) {
    popup.opener = null;
    return;
  }
  window.open(url, '_blank', 'noopener,noreferrer');
}

function TrackingLines({ tracking }) {
  return tracking.map((track, trackIdx) => (
    <div key={trackIdx} className="item-tracking-line">
      {track.displayStatus ? (
        <span className="item-tracking-status">
          {formatFulfillmentDisplayStatus(track.displayStatus) !== 'fulfilled' && (
            <span className="item-tracking-label">
              {formatFulfillmentDisplayStatus(track.displayStatus)}
            </span>
          )}
          {formatFulfillmentDisplayStatus(track.displayStatus) === 'delivered' && track.deliveredAt && (
            <span className="item-tracking-date">
              {track.deliveredAt ? ` ${formatDateOnly(track.deliveredAt)}` : ''}
            </span>
          )}
        </span>
      ) : null}
      {formatFulfillmentDisplayStatus(track.displayStatus) !== 'delivered' && track.estimatedDeliveryAt ? (
        <span className="item-tracking-delivered">
          <span className="item-tracking-date">Est. Delivery</span>{' '}
          {formatDateOnly(track.estimatedDeliveryAt)}
        </span>
      ) : null}
      {(track.number || track.url) && (
        <span className="item-tracking-number">
          <span className="item-tracking-label">{track.company ? `${track.company == "Other" ? "Tracking" : track.company}: ` : 'Tracking: '}</span>
          {track.url ? (
            <a
              href={track.url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => {
                e.preventDefault();
                openTrackingPopup(track.url);
              }}
            >
              {track.number || 'Track package'}
            </a>
          ) : (
            <span>{track.number}</span>
          )}
        </span>
      )}
    </div>
  ));
}

function LineItem({
  item,
  index,
  expandThreshold = 3,
  isExpanded = false,
  onToggleExpand,
  variant = 'card',
  // Detail-only
  isRemoved = false,
  onRemove,
  canRemove = false,
  displayQuantity,
  onQuantityChange,
  canEditQuantity = false,
  canEditLineItems = false,
  isQuantityLoading = false,
  showManual = false,
  // unfulfilledQuantity = null,
  isOrder = false,
  // Orders only: per-line-item estimated ship date (ISO yyyy-mm-dd) sourced from
  // the wg_fields.estimated_ship_date JSON metafield. Null/absent renders nothing,
  // so quote views are unaffected.
  estimatedShipDate = null,
}) {
  const filteredAttrs = item.customAttributes
    ? item.customAttributes.filter((attr) => ATTRIBUTE_KEY_FILTER(attr.key))
    : [];
  const hasMany = filteredAttrs.length > expandThreshold;
  const attrsToShow = hasMany && !isExpanded ? filteredAttrs.slice(0, expandThreshold) : filteredAttrs;
  const finalQty = isOrder ? item.currentQuantity : item.quantity;
  const qty = displayQuantity !== undefined ? displayQuantity : finalQty;

  const isFulfilled = isOrder && item.unfulfilledQuantity === 0;
  const hasTracking = isOrder && item.requiresShipping && item.tracking?.length > 0;
  const showShipColumn = isOrder && item.requiresShipping && !isFulfilled;
  const showCardQtyPrice = variant === 'card' || isOrder;
  const showQtyColumn = showCardQtyPrice || (variant === 'detail' && !isOrder);

  const isCancelled = item.currentQuantity === 0;

  const lineItemClass = [
    'line-item',
    `line-item--${variant}`,
    isOrder ? 'line-item--order' : 'line-item--quote',
    isRemoved ? 'line-item--removed' : '',
    item.tracking?.length > 0 ? 'line-item--has-tracking' : '',
    showQtyColumn ? 'line-item--has-qty' : '',
    isCancelled ? 'line-item--cancelled' : '',
  ]
  .filter(Boolean)
  .join(' ');

  const TitleContent = (
    <>
      {item.title}
      {item.variantTitle && !RESTRICTED_VENDORS.includes(item.vendor) && (
        <span className="variant-title">&nbsp;/&nbsp;{item.variantTitle}</span>
      )}
    </>
  );

  return (
    <div className={lineItemClass}>

      <div className="line-item-inner">
      <div className="line-item__media item-image">
        {isOrder && !isCancelled && item.unfulfilledQuantity === 0 && <CheckCircleIcon />}
        
        <div className="item-image-inner">
          <ProductImage
            canClick={canClickImage(item)}
            handle={item.handle}
            src={item.imageSquare200}
            alt={item.imageAltText}
            productType={item.productType}
            isFulfilled={isFulfilled}
          />
        </div>
      </div>

      <div className="item-title">

        <div className="item-title-inner">
          {canClickImage(item) ? (
            <a
              className="item-title-link"
              href={`${SHOP_URL}/products/${item.handle}?variant=${item.variantId}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              {TitleContent}
            </a>
          ) : (
            <span className="item-title-link">
              {TitleContent}
            </span>
          )}

          
        </div>
        

        {showManual && (item.manual || item.variantManual || item.packingList) && (
          <div className="item-manual">
            <a
              href={item.variantManual || item.manual}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => openPdfLink(e, item.variantManual || item.manual, 'Installation Manual')}
            >
              <PdfIcon strokeWidth={3.5} />
              Installation Manual
            </a>

            {showManual && item.packingList && (
              <a
                href={item.packingList}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => openPdfLink(e, item.packingList, 'Packing List')}
              >
                <PdfIcon strokeWidth={3.5} />
                Packing List
              </a>
              )}
          </div>
        )}

        {/* {showManual && item.packingList && (
          <div className="item-manual">
            <a
              href={item.packingList}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => openPdfLink(e, item.packingList, 'Packing List')}
            >
              <PdfIcon strokeWidth={3.5} />
              Packing List
            </a>
          </div>
        )} */}

        {item.sku && !RESTRICTED_VENDORS.includes(item.vendor) && (
          <div className="item-sku">SKU: {item.sku}</div>
        )}

        {/* {estimatedShipDate && item.unfulfilledQuantity !== 0 && (
          <div className="item-ship-date">
            Est. Ship Date:&nbsp;
            <span className="item-ship-date-value">{formatDateOnly(estimatedShipDate)}</span>
          </div>
        )} */}

        {filteredAttrs.length > 0 && (
          <>
            <div
              className={`item-custom-attributes expandable-content ${hasMany && !isExpanded ? 'item-custom-attributes--collapsed' : ''}`}
            >
              {attrsToShow.map((attr, attrIdx) => (
                <div key={attrIdx} className="custom-attribute">
                  <span className="attribute-key">{formatAttrKey(attr.key)}</span>:&nbsp;
                  <span className="attribute-value">{formatAttrValue(attr.value)}</span>
                </div>
              ))}
            </div>
            {hasMany && (
              <button
                className="btn-show-more-attributes"
                onClick={() => onToggleExpand?.(index)}
                type="button"
                aria-expanded={isExpanded}
              >
                <span className="expandable-content__toggle-icon" />
                <span className="expandable-content__toggle-text">
                  {isExpanded ? 'Show Less' : `Show ${filteredAttrs.length - expandThreshold} More`}
                </span>
              </button>
            )}
          </>
        )}
      </div>


      {isOrder && (hasTracking || estimatedShipDate !== null) && (
        <div className="line-item__logistics item-ship-date">
          {hasTracking ? (
            <div className="item-tracking">
              <TrackingLines tracking={item.tracking} />
            </div>
          ) : null}
          {showShipColumn && estimatedShipDate !== null ? (
            <div className="line-item__metric">
              <span className="line-item__metric-label">Est. Ship Date:&nbsp;</span>
              <span className="line-item__metric-value item-ship-date-value">
                {formatDateOnly(estimatedShipDate)}
              </span>
            </div>
          ) : null}
        </div>
      )}



      {(variant === 'card' || isOrder) && (
        <>
          <div className="item-quantity">Qty: {finalQty}</div>
            <>
              <div className="item-price-container">
                <div className="item-price">
                  {useFormatUSD(item.price * finalQty)}
                </div>

                {isFulfilled && !isCancelled && 
                  <span className="pill-button pill-button-yellow pill-button-small">fulfilled</span>
                }
                {isCancelled && (
                  <span className="pill-button pill-button-red pill-button-small">removed</span>
                )}
              </div>
            </>
        </>
      )}

      {variant === 'detail' && !isOrder && (
        <>
          {canEditQuantity && canEditLineItems ? (
            <div
              className={`quantity-selector quantity-selector__quote item-quantity${isQuantityLoading ? ' quantity-selector--loading' : ''}`}
              aria-busy={isQuantityLoading}
            >
              {isQuantityLoading && (
                <span className="quantity-selector__spinner" aria-hidden="true" />
              )}
              <button
                type="button"
                className="quantity-selector__button qty-minus"
                data-action="decrease-quantity"
                onClick={() => onQuantityChange?.(index, qty - 1)}
                disabled={isRemoved || !canEditQuantity || isQuantityLoading}
                aria-label="Decrease Quantity"
                title="Decrease Quantity"
              >
                -
              </button>
              <input
                id={`qty-${index}`}
                type="number"
                min="1"
                value={qty}
                onChange={(e) => onQuantityChange?.(index, parseInt(e.target.value, 10))}
                disabled={isRemoved || !canEditQuantity || isQuantityLoading}
                className="quantity-input quantity-selector__value"
              />
              <button
                type="button"
                className="quantity-selector__button qty-plus"
                data-action="increase-quantity"
                onClick={() => onQuantityChange?.(index, qty + 1)}
                disabled={isRemoved || !canEditQuantity || isQuantityLoading}
                aria-label="Increase Quantity"
                title="Increase Quantity"
              >
                +
              </button>
            </div>
          ) : (
            null
          )}

          <div className="item-price-container">
            <div className="item-price">
              {useFormatUSD(item.price * qty)}
            </div>
            {canRemove && (
              <button
                className={`btn-remove-item ${isRemoved ? 'btn-remove-item--undo' : ''}`}
                onClick={() => onRemove?.(index)}
                title={isRemoved ? 'Undo remove' : 'Remove item'}
              >
                {isRemoved ? 'Undo' : <HiOutlineTrash />}
              </button>
            )}
          </div>
        </>
      )}
      </div>
    </div>
  );
}

export default LineItem;
